import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import PhotoOutlinedIcon from '@mui/icons-material/PhotoOutlined';
import {
  getLotContentPresignedUrl,
  saveLotContentImage,
  deleteLotContentImage,
  getLotUserContent,
  setLotContentCoverImage,
} from '../../../../services/lotUserContentService';
import { toast } from '../../../../Components/Toast';
import { confirmDialog } from '../../../../Components/ConfirmDialog';
import ListingImageTile from './ListingImageTile';
import ListingImageCarousel from './ListingImageCarousel';
import ListingImageLightbox from './ListingImageLightbox';
import {
  LISTING_MAX_IMAGES,
  LISTING_MIN_IMAGES,
  LISTING_IMAGE_LIMIT_TOAST,
  LISTING_IMAGE_MIN_TOAST,
  LISTING_COVER_HINT,
  applyCoverImageToListingImages,
  normalizeListingImagesFromPayload,
  isListingImageLimitError,
  isListingImageMinError,
  resolveImageApiMessage,
} from '../../../utils/listingImageUtils';
import { resolveSpaceGuestWriteError } from '../../../utils/spaceMirrorLots';

const applyImages = (imagesOrPayload) => {
  if (Array.isArray(imagesOrPayload)) {
    return normalizeListingImagesFromPayload({ images: imagesOrPayload });
  }
  return normalizeListingImagesFromPayload(imagesOrPayload);
};

/**
 * Fotos del contenido de lote. `ownerUserId`: espacio ajeno (F2), sube al
 * contenido del owner (mismo owner en presign y save). `additiveOnly`: sin
 * borrar ni portada (el invitado solo suma; esas acciones son del dueño).
 */
const LotContentImages = ({
  lotId,
  canEdit,
  initialImages,
  onImagesChange,
  ownerUserId = null,
  additiveOnly = false,
}) => {
  const canRemove = Boolean(canEdit) && !additiveOnly;
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [deletingIds, setDeletingIds] = useState(new Set());
  const [settingCoverId, setSettingCoverId] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (Array.isArray(initialImages)) {
      setImages(applyImages(initialImages));
    }
  }, [initialImages]);

  const notifyChange = (next) => {
    const normalized = applyImages(next);
    setImages(normalized);
    onImagesChange?.(normalized);
  };

  const handleMultipleUpload = async (files) => {
    const fileArray = Array.from(files || []);
    if (fileArray.length === 0) return;

    const available = LISTING_MAX_IMAGES - images.length;
    if (available <= 0) {
      toast.warning(LISTING_IMAGE_LIMIT_TOAST);
      return;
    }
    if (fileArray.length > available) {
      toast.warning({
        title: 'Límite de fotos',
        message: `Solo podés agregar ${available} foto${available !== 1 ? 's' : ''} más.`,
      });
      return;
    }

    setUploading(true);
    let errors = 0;
    let hitLimit = false;
    let spaceError = null;
    let currentImages = [...images];
    try {
      for (const file of fileArray) {
        if (!file.type.startsWith('image/')) {
          errors++;
          continue;
        }
        try {
          const presignedRes = await getLotContentPresignedUrl(lotId, file.type, { ownerUserId });
          if (!presignedRes._httpOk || !presignedRes.data?.presignedUrl) {
            spaceError = resolveSpaceGuestWriteError(presignedRes._httpStatus, ownerUserId);
            if (spaceError) {
              errors++;
              break;
            }
            const msg = resolveImageApiMessage(presignedRes);
            if (isListingImageLimitError(presignedRes._httpStatus, msg)) {
              hitLimit = true;
              break;
            }
            throw new Error('No se obtuvo la URL de subida.');
          }
          const { presignedUrl, s3Url } = presignedRes.data;
          const uploadRes = await fetch(presignedUrl, {
            method: 'PUT',
            body: file,
            headers: { 'Content-Type': file.type },
          });
          if (!uploadRes.ok) throw new Error('Error al subir la imagen a S3.');
          const saveRes = await saveLotContentImage(lotId, s3Url, { ownerUserId });
          if (!saveRes._httpOk || !saveRes.data) {
            spaceError = resolveSpaceGuestWriteError(saveRes._httpStatus, ownerUserId);
            if (spaceError) {
              errors++;
              break;
            }
            const msg = resolveImageApiMessage(saveRes);
            if (isListingImageLimitError(saveRes._httpStatus, msg)) {
              hitLimit = true;
              break;
            }
            throw new Error('Error al guardar la imagen.');
          }
          currentImages = [...currentImages, saveRes.data];
          notifyChange(currentImages);
        } catch (e) {
          errors++;
          if (e.message === 'limit') {
            hitLimit = true;
            break;
          }
        }
      }
      if (spaceError) {
        toast.error({ title: 'Error', message: spaceError });
      } else if (hitLimit) {
        toast.warning(LISTING_IMAGE_LIMIT_TOAST);
      } else if (errors === 0 && fileArray.length > 0) {
        toast.success(`${fileArray.length > 1 ? 'Fotos subidas' : 'Foto subida'} correctamente.`);
      } else if (errors > 0 && errors < fileArray.length) {
        toast.warning({
          title: 'Parcialmente completado',
          message: `${fileArray.length - errors} foto(s) subidas. ${errors} no pudieron subirse.`,
        });
      } else if (errors === fileArray.length) {
        toast.error({ title: 'Error', message: 'No se pudieron subir las fotos.' });
      }
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDelete = async (image) => {
    if (!canRemove) return;
    if (images.length <= LISTING_MIN_IMAGES) {
      toast.warning(LISTING_IMAGE_MIN_TOAST);
      return;
    }

    const result = await confirmDialog({
      title: '¿Eliminar foto?',
      description: 'Se eliminará definitivamente. Esta acción no se puede deshacer.',
      confirmLabel: 'Eliminar',
      cancelLabel: 'Cancelar',
      variant: 'danger',
    });
    if (!result.isConfirmed) return;

    setDeletingIds((prev) => new Set([...prev, image.id]));
    const prevImages = images;
    notifyChange(images.filter((img) => img.id !== image.id));

    try {
      const res = await deleteLotContentImage(image.id);
      if (!res._httpOk) {
        const msg = resolveImageApiMessage(res);
        if (isListingImageMinError(res._httpStatus, msg)) {
          toast.warning(LISTING_IMAGE_MIN_TOAST);
        } else {
          toast.error({ title: 'Error', message: 'No se pudo eliminar la foto. Intentá de nuevo.' });
        }
        throw new Error('delete failed');
      }
      notifyChange(applyCoverImageToListingImages(
        prevImages.filter((img) => img.id !== image.id),
        res.data?.cover_image ?? null,
      ));
    } catch (e) {
      if (e.message !== 'delete failed') {
        console.error('Error deleting image:', e);
        toast.error({ title: 'Error', message: 'No se pudo eliminar la foto. Intentá de nuevo.' });
      }
      notifyChange(prevImages);
    } finally {
      setDeletingIds((prev) => {
        const next = new Set(prev);
        next.delete(image.id);
        return next;
      });
    }
  };

  const refreshImages = async () => {
    const res = await getLotUserContent(lotId, { ownerUserId });
    if (!res._httpOk) return false;
    notifyChange(normalizeListingImagesFromPayload(res.data ?? {}));
    return true;
  };

  const handleSetCover = async (image) => {
    if (!canRemove) return;
    if (!image?.id || image.is_cover || images.length <= 1) return;

    setSettingCoverId(image.id);
    try {
      const res = await setLotContentCoverImage(image.id, lotId);
      if (!res._httpOk) {
        toast.error({
          title: 'Error',
          message: resolveImageApiMessage(res) || 'No se pudo cambiar la portada.',
        });
        return;
      }

      const refreshed = await refreshImages();
      if (!refreshed) {
        notifyChange(applyCoverImageToListingImages(images, res.data?.cover_image ?? null));
      }
      toast.success('Portada actualizada.');
    } catch (e) {
      console.error('Error setting cover image:', e);
      toast.error({ title: 'Error', message: 'No se pudo cambiar la portada.' });
    } finally {
      setSettingCoverId(null);
    }
  };

  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
        {images.length > 0 ? (
          canEdit ? (
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1 }}>
              {images.map((image) => (
                <ListingImageTile
                  key={image.id}
                  image={image}
                  canEdit={canRemove}
                  isDeleting={deletingIds.has(image.id)}
                  isSettingCover={settingCoverId === image.id}
                  showSetCover={canRemove && images.length > 1 && !image.is_cover}
                  onOpen={(opened) => {
                    const next = images.findIndex((item) => item.id === opened.id);
                    setLightboxIndex(next >= 0 ? next : 0);
                  }}
                  onDelete={handleDelete}
                  onSetCover={handleSetCover}
                />
              ))}
            </Box>
          ) : (
            <ListingImageCarousel images={images} />
          )
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.5,
              py: 2.5,
              borderRadius: 1.5,
              border: '1px dashed rgba(255,255,255,0.12)',
              bgcolor: 'rgba(255,255,255,0.02)',
            }}
          >
            <PhotoOutlinedIcon sx={{ fontSize: 22, color: 'rgba(255,255,255,0.28)' }} />
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '11px' }}>
              Sin fotos
            </Typography>
          </Box>
        )}

        {canEdit && images.length < LISTING_MAX_IMAGES && (
          <Box>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              style={{ display: 'none' }}
              onChange={(e) => handleMultipleUpload(e.target.files)}
            />
            <Box
              onClick={() => !uploading && fileInputRef.current?.click()}
              sx={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                gap: 1, p: 1.5,
                border: '1px dashed rgba(255,255,255,0.15)',
                borderRadius: 1.5,
                cursor: uploading ? 'default' : 'pointer',
                color: 'rgba(255,255,255,0.4)',
                fontSize: '12px',
                fontFamily: 'inherit',
                transition: 'all 0.15s ease',
                '&:hover': uploading ? {} : {
                  borderColor: 'rgba(255,255,255,0.30)',
                  color: 'rgba(255,255,255,0.7)',
                  bgcolor: 'rgba(255,255,255,0.03)',
                },
              }}
            >
              {uploading ? (
                <>
                  <CircularProgress size={14} sx={{ color: 'rgba(255,255,255,0.4)' }} />
                  <Typography variant="caption" sx={{ color: 'inherit', fontFamily: 'inherit' }}>
                    Subiendo...
                  </Typography>
                </>
              ) : (
                <>
                  <AddPhotoAlternateIcon sx={{ fontSize: 16 }} />
                  <Typography variant="caption" sx={{ color: 'inherit', fontFamily: 'inherit' }}>
                    Agregar foto ({images.length}/{LISTING_MAX_IMAGES})
                  </Typography>
                </>
              )}
            </Box>
          </Box>
        )}

        {canEdit && images.length >= LISTING_MAX_IMAGES && (
          <Typography variant="caption" sx={{
            color: 'rgba(255,255,255,0.30)', fontSize: '11px',
            textAlign: 'center', fontStyle: 'italic',
          }}
          >
            Límite de {LISTING_MAX_IMAGES} fotos alcanzado
          </Typography>
        )}

        {canRemove && images.length > 1 && (
          <Typography variant="caption" sx={{
            color: 'rgba(255,255,255,0.28)', fontSize: '10px',
            textAlign: 'center',
          }}
          >
            {LISTING_COVER_HINT}
          </Typography>
        )}
      </Box>

      {canEdit && lightboxIndex != null ? (
        <ListingImageLightbox
          images={images}
          index={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onIndexChange={setLightboxIndex}
        />
      ) : null}
    </>
  );
};

export default LotContentImages;
